import React from 'react';
import PanelHeader from './PanelHeader';
import { AbaDaCasca, AbasDaCasca, FaixaDaCasca, NumeroDaFaixa } from './CascaDeModal';
import { useFecharNoEsc } from './useFecharNoEsc';
import { useMudouNoCorpo } from './useMudouNoCorpo';
import { useVoltarFecha } from './useVoltarFecha';
import PerguntaDeFechar from './PerguntaDeFechar';

/**
 * A casca das GAVETAS: a mesma gramática da CascaDeModal (cabeçalho com título,
 * linha mono e ×; selos; aviso; abas neutras; faixa de números; corpo que rola;
 * rodapé com UMA primária laranja), presa à direita e na altura da tela.
 * Prévia aprovada pelo Heitor em 05/10/2026: https://claude.ai/artifact/F1jdpo5Um78SVajA6aWCcD
 *
 * Decisões próprias de gaveta (as da prévia):
 * - Lado: sempre à direita, altura total.
 * - Cobre, com fundo escurecido; não empurra. Empurrar espremeria as tabelas largas
 *   do Encontrar e do Cadastrar sem ganho: a gaveta é foco temporário.
 * - Três larguras, as que as gavetas já usavam (ver LARGURA).
 * - Clique fora e Esc seguem a regra da CascaDeModal: só fecham gaveta de LEITURA
 *   (`fecharAoClicarFora`). Formulário fecha pelo × ou pelo Cancelar.
 * - No telefone, largura total.
 *
 * O fundo fecha no mousedown com alvo conferido, não no click: um arrasto que
 * começa dentro da gaveta (selecionar o objeto da compra, por exemplo) e termina
 * fora dispara click no fundo, e fecharia a gaveta debaixo do operador.
 */

interface Props {
  titulo: React.ReactNode;
  /** Dica do título quando ele é longo e trunca (nome do órgão). */
  tituloDica?: string;
  /** Linha mono de identificação: nº · CNPJ. */
  subtitulo?: React.ReactNode;
  /** Selos sob o título (status, ME/EPP, portal, fonte). */
  selos?: React.ReactNode;
  onClose: () => void;
  subCabecalho?: React.ReactNode;
  /** Avisos que valem para a gaveta inteira, fora da rolagem. */
  aviso?: React.ReactNode;
  abas?: AbaDaCasca[];
  abaAtiva?: string;
  onAba?: (chave: string) => void;
  faixa?: NumeroDaFaixa[];
  topo?: React.ReactNode;
  children: React.ReactNode;
  rodape?: React.ReactNode;
  /** `ficha`: detalhe e proposta. `painel`: ranking. `lista`: revisão de duplicatas. */
  largura?: 'ficha' | 'painel' | 'lista';
  corpoClassName?: string;
  zIndex?: string;
  /** Gaveta de leitura fecha no fundo e no Esc. Padrão `false`, como na CascaDeModal:
   *  formulário não pode perder o que foi digitado num clique fora. */
  fecharAoClicarFora?: boolean;
  /** `false` = operação em curso: sem ×, sem fundo, sem Esc. */
  fechavel?: boolean;
  /** Rótulo da gaveta para leitor de tela (o título pode ser um nó). */
  rotulo?: string;
  /** Formulário: há algo digitado que o fechar perderia? Mesma regra da CascaDeModal: `false` fecha no Esc direto;
   *  `true` ou ausente pergunta "Fechar sem salvar?" antes. */
  temEdicaoNaoSalva?: boolean | (() => boolean);
  /** A URL enquanto a casca está aberta (ex. "/clientes?cliente=5"): o link copiado e o F5 reabrem. Ver useVoltarFecha. */
  endereco?: string;
}

const LARGURA = {
  ficha: 'w-full lg:w-[62vw] lg:max-w-[1180px]',
  painel: 'w-full sm:max-w-2xl',
  lista: 'w-full sm:w-[520px]',
};

export default function CascaDeGaveta({
  titulo, tituloDica, subtitulo, selos, onClose, subCabecalho, aviso, abas, abaAtiva, onAba, faixa, topo,
  children, rodape, largura = 'ficha', corpoClassName = 'px-5 py-4', zIndex = 'z-50',
  fecharAoClicarFora = false, fechavel = true, rotulo, temEdicaoNaoSalva, endereco,
}: Props) {
  const fechaNoFundo = fecharAoClicarFora && fechavel;
  const [perguntando, setPerguntando] = React.useState(false);
  // O Esc, como na CascaDeModal (09/10/2026): leitura fecha; formulário pergunta, a não ser que diga que não há edição;
  // operação em curso engole.
  const { corpoRef, mudou: mudouNoCorpo } = useMudouNoCorpo();
  const aoSair = !fechavel ? null : fechaNoFundo ? onClose : () => {
    // Sem a tela dizer, a casca olha o corpo contra o estado em que abriu (useMudouNoCorpo, 16/10/2026).
    const sujo = temEdicaoNaoSalva === undefined ? mudouNoCorpo() : typeof temEdicaoNaoSalva === 'function' ? temEdicaoNaoSalva() : temEdicaoNaoSalva;
    if (sujo === false) onClose(); else setPerguntando(true);
  };
  useFecharNoEsc(aoSair);
  // O Voltar do navegador faz o MESMO que o Esc (14/10/2026, opção (a) do Heitor): fecha a casca antes de trocar de tela.
  useVoltarFecha(aoSair, endereco);
  return (
    <div
      className={`fixed inset-0 bg-black/60 ${zIndex}`}
      onMouseDown={fechaNoFundo ? (e) => { if (e.target === e.currentTarget) onClose(); } : undefined}
      data-testid='gaveta-fundo'
    >
      <aside
        className={`absolute right-0 top-0 h-full ${LARGURA[largura]} bg-central-cartao border-l border-central-borda shadow-2xl flex flex-col overflow-hidden motion-safe:animate-gaveta-entra`}
        role='dialog'
        aria-modal='true'
        aria-label={rotulo || (typeof titulo === 'string' ? titulo : undefined)}
      >
        <PanelHeader
          className='shrink-0 px-5'
          title={titulo}
          titleTooltip={tituloDica}
          subtitle={subtitulo}
          badges={selos}
          onClose={fechavel ? onClose : undefined}
        />

        {subCabecalho && <div className='shrink-0'>{subCabecalho}</div>}

        {aviso && <div className='shrink-0'>{aviso}</div>}

        {abas && abas.length > 0 && <AbasDaCasca abas={abas} abaAtiva={abaAtiva} onAba={onAba} />}

        {faixa && faixa.length > 0 && <FaixaDaCasca faixa={faixa} />}

        {topo && <div className='shrink-0'>{topo}</div>}

        <div className={`flex-1 min-h-0 overflow-y-auto ${corpoClassName}`} ref={corpoRef}>{children}</div>

        {rodape && <div className='shrink-0 flex flex-wrap items-center gap-2 px-5 py-3 border-t border-central-borda'>{rodape}</div>}
      </aside>
      {perguntando && <PerguntaDeFechar onContinuar={() => setPerguntando(false)} onFechar={() => { setPerguntando(false); onClose(); }} />}
    </div>
  );
}
